import { bufferToHexString } from '@iotize/common/byte-converter';
import { KaitaiStreamWriter } from '@iotize/common/byte-stream';
import { Encoder, EncoderDecoder } from '@iotize/common/converter/api';
import { getAllDecorators, getMethods } from '@iotize/common/decorators';
import { deepCopy, sleep } from '@iotize/common/utility';
import { PathParameter } from '@iotize/tap';
import { hashLoginPassword, TapAuth } from '@iotize/tap/auth';
import {
  ResultCode,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';
import {
  TapRequestHelper,
  TapResponseFrameBuilder,
  TAP_REQUEST_FRAME_CONVERTER,
  TAP_RESPONSE_FRAME_CONVERTER,
} from '@iotize/tap/client/impl';
import {
  AnonymousProfileConfig,
  SecurityOptionsConfig,
  SpecialProfileConfig,
  TapConfig,
  UserConfig,
} from '@iotize/tap/config/schema/v1';
import { FirmwareState } from '@iotize/tap/service/impl/firmware';
import { LoginCredential } from '@iotize/tap/service/impl/interface';
import { Subject } from 'rxjs';
import {
  GetMetaData,
  GET_META_DATA_KEY,
  PostMetaData,
  POST_META_DATA_KEY,
  PutMetaData,
  PUT_META_DATA_KEY,
} from './annotations';
import { VirtualTarget } from './api/virtual-target';
import { DEFAULT_DEVICE_MODEL_NAME, DEFAULT_SESSION_LIFETIME } from './config';
import { ConfigExplorer, resolveProfile } from './config-explorer';
import { ascii } from './converters';
import { DataLogEngine } from './datalog-engine';
import { debug } from './debug';
import { ServerComProtocol } from './server-com-protocol';
import { BundleService } from './service/bundle-service';
import { CloudService } from './service/cloud-service';
import { DatalogService } from './service/datalog-service';
import { DeviceService } from './service/device-service';
import { FirmwareService } from './service/firmware-service';
import { GroupService } from './service/group-service';
import { InterfaceService } from './service/interface-service';
import { JvmService } from './service/jvm-service';
import { MqttRelayService } from './service/mqtt-service';
import { ScramService } from './service/scram-service';
import { SinglePacketService } from './service/single-packet-service';
import { TargetService } from './service/target-service';
import { VariableService } from './service/variable-service';
import { WifiService } from './service/wifi-service';
import { TapServerError } from './tap-server-error';
import { VirtualTapConfig } from './virtual-tap';
import { VirtualTargetDataCom } from './virtual-target';

const TAG = 'TapServer';

const DEFAULT_SECURITY_OPTIONS: SecurityOptionsConfig = {
  scramActivated: false,
  hashPassword: false,
  disableHardwareFactoryReset: false,
  disableLoginWithUID: false,
  disableResourceFactoryReset: false,
};

const DEFAULT_SESSION_STATE: TapAuth.SessionState = {
  groupId: 0,
  lifeTime: -1,
  name: 'anonymous',
  startTime: new Date(),
};

export interface CallContext<T = Uint8Array> {
  pathParameters: Record<string, number>;
  server: VirtualTap;
  body: T;
  rawBody: Uint8Array;
  protocol: ServerComProtocol;
  getPathParameter(name: string): number;
}

type RequestMeta<T = any> = GetMetaData<T> | PostMetaData<T> | PutMetaData<T>;
interface Endpoint<T = Uint8Array> {
  call: (context: CallContext<T>) => any;
  meta: RequestMeta;
}

interface EndpointCandidate {
  endpoint: Endpoint;
  pathParameters: Record<string, number>;
}

interface RequestContext {
  protocol: ServerComProtocol;
}

export function checkRouteMatch(
  request: { method: TapRequestFrame.MethodType; path: string },
  meta: RequestMeta<any>
) {
  if (meta.method !== request.method) {
    return false;
  }
  const parameters = PathParameter.extractParams(meta.path);
  if (parameters.length > 0) {
    let routePattern = meta.path.replace(/\//g, '\\/');
    for (const parameter of parameters) {
      routePattern = routePattern.replace(`{${parameter}}`, '([0-9]*)');
    }
    // debug(TAG, `Route with param "${routePattern}"`)
    return !!new RegExp(routePattern).exec(request.path);
  }
  return meta.path === request.path;
}

export function getRouteParams(
  request: { method: TapRequestFrame.MethodType; path: string },
  meta: RequestMeta<any>
) {
  const parameters = PathParameter.extractParams(meta.path);
  const pathParameterMap: Record<string, number> = {};
  const pathParts = meta.path.split('/');
  const requestParts = request.path.split('/');
  for (const parameter of parameters) {
    const paramIndex = pathParts.findIndex((v) => v === `{${parameter}}`);
    if (paramIndex < 0) {
      throw TapServerError.internalError(
        `Internal error: cannot find ${parameter} in path ${meta.path}`
      );
    }
    if (paramIndex >= requestParts.length) {
      throw TapServerError.internalError(
        `Request path ${request.path} should not be tested again ${meta.path}`
      );
    }
    if (requestParts[paramIndex] === '') {
      pathParameterMap[parameter] = 0xffff;
    } else {
      pathParameterMap[parameter] = parseInt(requestParts[paramIndex], 10);
    }
  }
  return pathParameterMap;
}

export const FACTORY_RESET_CONFIG: TapConfig = {
  version: '0.0.0',
  application: {
    name: 'TAP',
  },
};

function arrayEquals(
  array1: any[] | Uint8Array | string,
  array2: any[] | Uint8Array | string
) {
  if (array1.length !== array2.length) {
    return false;
  }
  for (let i = 0; i < array1.length; i++) {
    if (array1[i] !== array2[i]) {
      return false;
    }
  }
  return true;
}
export class VirtualTap {
  public protocols: ServerComProtocol[] = [];

  public requestDecoder = TAP_REQUEST_FRAME_CONVERTER;
  public responseEncoder = TAP_RESPONSE_FRAME_CONVERTER;

  public sessionState: TapAuth.SessionState = DEFAULT_SESSION_STATE;

  public requestStream = new Subject<Uint8Array>();
  public responseStream = new Subject<Uint8Array>();

  public endpoints: Endpoint<any>[] = [];

  public info = new VirtualTap.Info();

  public configExplorer: ConfigExplorer;

  public targetCom: VirtualTargetDataCom;

  public dataLogEngine: DataLogEngine;

  private _firmwareState = FirmwareState.NORMAL;

  public get firmwareVersion() {
    return this.deviceConfig.firmwareVersion || '2.12 Apr 10 2022 11:43:50';
  }

  public get serialNumber() {
    return this.deviceConfig.serialNumber;
  }

  public get modelName() {
    return this.deviceConfig.modelName || DEFAULT_DEVICE_MODEL_NAME;
  }

  public get securityOptions() {
    return this.config.tap?.security || DEFAULT_SECURITY_OPTIONS;
  }

  get config() {
    return this.configExplorer.config;
  }

  setConfig(configSchema: TapConfig) {
    this.configExplorer.config = configSchema;
  }

  static create(
    serverConfig: VirtualTapConfig = {
      serialNumber: 'IoTVI006100000000',
    },
    tapConfig?: TapConfig
  ) {
    if (!tapConfig) {
      tapConfig = deepCopy(FACTORY_RESET_CONFIG);
    }
    const tap = new VirtualTap(tapConfig, serverConfig);
    tap.registerService([
      BundleService,
      CloudService,
      DatalogService,
      DeviceService,
      FirmwareService,
      GroupService,
      InterfaceService,
      MqttRelayService,
      ScramService,
      TargetService,
      VariableService,
      WifiService,
      JvmService,
      SinglePacketService,
    ]);
    return tap;
  }

  constructor(configSchema: TapConfig, public deviceConfig: VirtualTapConfig) {
    // this.requestStream.subscribe((msg) => {
    //     this.handleData(msg, {
    //         protocol: null as any
    //     });
    // });
    if (!configSchema.data) {
      configSchema.data = {};
    }
    if (!configSchema.data.bundles) {
      configSchema.data.bundles = [];
    }
    const configExplorer = new ConfigExplorer(configSchema);
    this.configExplorer = configExplorer;
    this.targetCom = new VirtualTargetDataCom(configExplorer);
    this.dataLogEngine = new DataLogEngine(this.targetCom);
  }

  setFirmwareState(firmwareState: FirmwareState) {
    this._firmwareState = firmwareState;
  }

  getFirmwareState() {
    return this._firmwareState;
  }

  setTarget(target: VirtualTarget) {
    this.targetCom.setTarget(target);
  }

  getHostName() {
    return '0.0.0.0';
  }

  getMacAddress() {
    return '00:00:00:00:00:00';
  }

  getHostProtocols() {
    return this.protocols.map((p) => p.getType());
  }

  login(data: LoginCredential) {
    const securityOptions = this.config.tap?.security;
    if (!securityOptions?.scramActivated) {
      const username = data.username.trim();
      let givenPassword = ascii.encode(data.password);
      const userOrProfile = this.configExplorer.allUserOrProfiles.find((p) => {
        debug(`Test tap profile ${p.username}`);
        return p.username.padEnd(16, '\0') === username;
      });
      if (!userOrProfile) {
        debug(`profile "${username}" not found`);
        throw TapServerError.requestError(ResultCode.UNAUTHORIZED);
      }
      let expectedPassword: Uint8Array;
      if (userOrProfile.key && userOrProfile.key.length === 16) {
        expectedPassword = userOrProfile.key;
      } else {
        const passwordString = userOrProfile.password || userOrProfile.username;
        if (securityOptions?.hashPassword) {
          expectedPassword = hashLoginPassword(passwordString.padEnd(16, '\0')); //bufferToAsciiString(passwordHasher.hash(expectedPassword));
        } else {
          expectedPassword = ascii.encode(passwordString);
        }
      }
      if (expectedPassword.length < 16) {
        expectedPassword = KaitaiStreamWriter.create(16)
          .writeBytes(expectedPassword)
          .forward(16 - expectedPassword.length).toBytes;
      }
      // if (profile.key) {
      //     expectedPassword = profile.key;
      // }
      // else {
      // givenPassword = ascii.encode(givenPassword);
      // const rawPassword = (profile.password || profile.username);
      // else {
      //     expectedPassword = rawPassword.padEnd(16, '\0');
      // }
      // }
      if (!arrayEquals(expectedPassword, givenPassword)) {
        debug(
          `profile "${givenPassword}" invalid password: "${givenPassword}" (expected "${expectedPassword}", hash password: ${!!securityOptions?.hashPassword})`
        );
        throw TapServerError.requestError(ResultCode.UNAUTHORIZED);
      }
      debug(`login as ${username} successfull!`);
      this.setSessionStateFromProfile(userOrProfile);
    } else {
      throw TapServerError.notImplemented();
    }
  }

  private setSessionStateFromProfile(
    profile: SpecialProfileConfig | AnonymousProfileConfig | UserConfig
  ) {
    this.sessionState = {
      groupId: resolveProfile(profile).id,
      lifeTime: profile.lifeTime || DEFAULT_SESSION_LIFETIME,
      name: profile.username,
      startTime: new Date(),
    };
    debug('New session state:', this.sessionState);
  }

  logout() {
    this.sessionState = DEFAULT_SESSION_STATE;
  }

  listen() {
    return new Promise(() => {});
  }

  async start() {
    if (this.protocols.length === 0) {
      throw new Error(`No protocol registered. Server cannot start`);
    }

    const { errorProtocols, startedProtocls } = await this._startProtocols();
    if (startedProtocls.length === 0) {
      throw new Error(`None of the given protocool successfully started`);
    }
    errorProtocols.forEach((p) => this.rescheduleProtocolStart(p));

    if (
      this.dataLogEngine &&
      this.configExplorer.bundles.filter((b) => (b.dataLog?.period || 0) > 0)
        .length > 0
    ) {
      try {
        this.targetCom.connect();
        this.dataLogEngine.start();
      } catch (err) {
        console.warn('Failed to start datalog', err);
      }
    }
  }

  private rescheduleProtocolStart(p: ServerComProtocol) {
    setTimeout(() => {
      p.start()
        .then((_) => console.log('Protocol started at second try'))
        .catch((err) => {
          console.warn(
            `Cannot start protocol ${p.debugString()} after second try...`
          );
        });
    }, 30 * 1000);
  }

  async stop() {
    debug('Stoping server...');
    debug('\t- Stoping protocols...');
    for (const p of this.protocols) {
      try {
        debug(`Stop protocol ${p.debugString()}`);
        await p.stop(); // TODO stop with timeout
      } catch (err) {
        console.error(
          'Cannot stop protocol:',
          p.debugString(),
          (err as Error).message
        );
      }
    }
    debug('\t- Stoping target communication...');
    await this.targetCom.stop();
    debug('Server stopped!');
  }

  registerService(services: any[]) {
    this._buildServices(services);
  }

  registerHostProtocol(protocol: ServerComProtocol) {
    protocol.connections.subscribe({
      next: (newConnection) => {
        newConnection.data.subscribe({
          next: async (data: Uint8Array) => {
            try {
              debug(`${protocol.debugString()} Received request: `, data);
              // TODO add source of data ? socket/websocket...
              if (this.deviceConfig.sendDelay) {
                await sleep(this.deviceConfig.sendDelay);
              }
              const response = this.handleData(data, {
                protocol,
              });
              const encoded = this._encodeResponse(response);
              debug(
                `${protocol.debugString()} TapResponseFrame => ${response} => ${encoded}`
              );
              await newConnection.send(encoded);
            } catch (err) {
              console.warn(protocol.debugString(), 'error', err);
            }
          },
        });
      },
    });
    this.protocols.push(protocol);
  }

  _encodeResponse(response: TapResponseFrame) {
    return this.responseEncoder.encode(response);
  }

  public onNewConnection() {}

  factoryReset() {
    this.configExplorer.config = deepCopy(FACTORY_RESET_CONFIG);
  }

  async reboot() {
    for (const p of this.protocols) {
      try {
        await p.stop();
      } catch (err: any) {
        console.warn(
          `Failed to stop protocol ${p.debugString()} properly. Error: ${
            err.message
          }`,
          err
        );
      }
    }
    await this.start();
  }

  public handleData(
    data: Uint8Array,
    context: RequestContext
  ): TapResponseFrame {
    let request: TapRequestFrame;
    try {
      request = this.requestDecoder.decode(data);
    } catch (err) {
      debug(
        `Cannot decode input frame: ${bufferToHexString(data)}: ${err}`,
        err
      );
      return TapResponseFrameBuilder.ERROR(ResultCode.BAD_REQUEST);
    }
    try {
      return this.handleRequest(request, context);
    } catch (err) {
      debug(`Internal server error: ${err}`, err);
      console.warn(`Internal server error`, err);
      return TapResponseFrameBuilder.ERROR(ResultCode.INTERNAL_SERVER_ERROR);
    }
  }
  private _buildServices(services: any[]) {
    debug(`Building services...`);

    this.endpoints = [];
    services.forEach((service) => {
      // debug(`Service `, service);
      const serviceInstance = new service(this);
      const methods = getMethods(serviceInstance, Infinity);
      // console.info({
      //     methods
      // });
      const decorators = getAllDecorators(serviceInstance, methods);
      // console.info({
      //     decorators
      // })
      decorators.forEach((info) => {
        // debug(`Info `, info);
        const mapping = info.decorators
          .filter(
            (name) =>
              name === GET_META_DATA_KEY ||
              name === PUT_META_DATA_KEY ||
              name === POST_META_DATA_KEY
          )
          .map((decoratorName: Symbol) => {
            const meta = Reflect.getMetadata(
              decoratorName,
              serviceInstance,
              info.propertyName
            );
            if (!meta) {
              throw new Error(
                `No meta data for ${decoratorName.toString()} on prop ${
                  info.propertyName
                }`
              );
            }
            // debug('Adding ', meta.method, meta.path)
            return {
              call: serviceInstance[info.propertyName].bind(serviceInstance),
              meta: meta,
            };
          });
        this.endpoints.push(...mapping);
      });
    }, []);
    debug(
      'Endpoints: ',
      this.endpoints
        .map(
          (info) =>
            `\t- ${TapRequestFrame.MethodType[info.meta.method as any]} ${
              info.meta.path
            }`
        )
        .join('\r\n')
    );
  }

  public handleRequest(
    request: TapRequestFrame,
    context: RequestContext
  ): TapResponseFrame {
    const requestPath = TapRequestHelper.pathToString(request.header.path);
    const method = request.header.methodType;

    debug(`Handling request: ${TapRequestHelper.toString(request)}...`);

    if (!this.endpoints) {
      throw new Error(`Endpoint not initialized`);
    }
    const requestRouteMeta = {
      path: requestPath,
      method: method,
    };

    const candidate = this.endpoints.filter((endpoint) => {
      return checkRouteMatch(requestRouteMeta, endpoint.meta);
    });
    if (candidate.length > 1) {
      debug(`Multiple endpoint match for ${request}.`, candidate);
      throw new Error(`Multiple endpoint match for ${request}`);
    } else if (candidate.length === 1) {
      debug(`Found matching route!`);
      const response = this.handleEndpoint(
        request,
        {
          endpoint: candidate[0],
          pathParameters: getRouteParams(requestRouteMeta, candidate[0].meta),
        },
        context
      );
      debug(
        `Request: '${request.toString()}' => TapResponseFrame: ${response.toString()}`
      );
      return response;
    }
    const allPropertiesDecorators = getAllDecorators(
      this.info,
      Object.keys(this.info)
    );
    const annotedRoute = allPropertiesDecorators
      .filter((info) => {
        return (
          info.decorators.find((d) => d === GET_META_DATA_KEY) !== undefined
        );
      })
      .map((info) => {
        return {
          propertyName: info.propertyName,
          meta: Reflect.getMetadata(
            GET_META_DATA_KEY,
            this.info,
            info.propertyName
          ) as GetMetaData<any>,
        };
      })
      .find((route) => {
        return checkRouteMatch(requestRouteMeta, route.meta);
      });
    if (annotedRoute) {
      debug(`Found annoted route`, annotedRoute);
      return this.createResponse({
        converter: annotedRoute.meta.responseType as any, // TODO
        value: this.info[annotedRoute.propertyName as keyof VirtualTap.Info],
      });
    }
    debug(`Cannot find route matching ${request.toString()}`);
    return TapResponseFrameBuilder.ERROR(ResultCode.NOT_IMPLEMENTED);
  }

  handleEndpoint(
    request: TapRequestFrame,
    candidate: EndpointCandidate,
    context: RequestContext
  ): TapResponseFrame {
    try {
      debug(
        `${TapRequestHelper.toString(request)} pathParameters`,
        candidate.pathParameters,
        request.payload
      );
      const endpoint = candidate.endpoint;
      if (endpoint.meta.auth) {
        this.checkCredentials(endpoint.meta.auth);
      }
      if (endpoint.meta.configModeRequired) {
        this.checkConfigModeEnabled();
      }
      const rawBody = request.payload;
      let data: Uint8Array;
      try {
        data = endpoint.meta.payloadType
          ? endpoint.meta.payloadType.decode(rawBody)
          : rawBody;
      } catch (err) {
        debug(`Failed to decode body`, rawBody, err);
        throw TapServerError.badRequest();
      }
      let responseBody: any;
      responseBody = endpoint.call({
        getPathParameter: (name: string) => {
          if (!(name in candidate.pathParameters)) {
            debug(`Missing path parameter: ${name}`);
            throw TapServerError.badRequest();
          }
          return candidate.pathParameters[name];
        },
        body: data,
        rawBody,
        pathParameters: candidate.pathParameters,
        server: this,
        protocol: context.protocol,
      });
      debug(
        `Generated response body: ${responseBody}. Use converter: ${endpoint.meta.responseType?.constructor.name}`
      );
      const responseDataBytes: Uint8Array =
        endpoint.meta.responseType?.encode(responseBody) ||
        (responseBody as Uint8Array);
      const response = TapResponseFrameBuilder.create(
        ResultCode.CONTENT,
        responseDataBytes
      );
      // debug(`TapResponseFrame: ${response}`, {responseDataBytes});
      return response;
    } catch (err) {
      let errorCode: number;
      debug(`${request.toString()} error handle endpoint`, err);
      if (
        // instanceof does not work when loaded as esm2015 module
        err instanceof TapServerError ||
        typeof (err as TapServerError).resultCode === 'number'
      ) {
        errorCode = (err as TapServerError).resultCode;
      } else {
        console.warn(
          `Unexpected error when processing request ${request.toString()}`,
          err
        );
        errorCode = ResultCode.INTERNAL_SERVER_ERROR;
      }
      return TapResponseFrameBuilder.ERROR(errorCode);
    }
  }

  checkCredentials(auth: string[]) {
    for (const profileName of auth) {
      if (this.sessionState.name === profileName) {
        return;
      }
    }
    throw TapServerError.unauthorized();
  }

  checkConfigModeEnabled() {
    if (this.config.version !== '255.255.65535') {
      console.warn(
        `Request requires config mode to be enabled. Current config version: "${this.config.version}"`
      );
      throw TapServerError.notAvailable();
    }
  }

  sendErrorResponse(code: number) {
    return this.sendResponse(TapResponseFrameBuilder.ERROR(code));
  }

  public createResponse<T>(data: {
    converter: Encoder<T, Uint8Array>;
    value: T;
  }) {
    debug(`sendResponseData`, data);
    const encodedData = data.converter.encode(data.value);
    return TapResponseFrameBuilder.SUCCESS(encodedData);
  }

  public sendResponse<T>(response: TapResponseFrame) {
    debug(`sendResponse`, response);
    const encodedResponse = this.responseEncoder.encode(response);
    return this.sendData(encodedResponse);
  }

  public sendData(data: Uint8Array) {
    this.responseStream.next(data);
    return data;
  }

  public bindConfigToRoute<T, Key extends keyof T>(options: {
    path: string;
    item: (call: CallContext<any>) => T;
    key: Key;
    default: T[Key];
    converter?: EncoderDecoder<T[Key], Uint8Array>;
    get?: boolean;
    put?: boolean;
    post?: boolean;
  }) {
    if (!options.item) {
      throw new Error(
        `Cannot bind route ${options.path} to undefined config item`
      );
    }
    function getItem(context: CallContext<any>) {
      const item =
        typeof options.item === 'function'
          ? (options.item as Function)(context)
          : options.item;
      if (typeof item !== 'object') {
        debug(`Invalid item type "${typeof item}"`, options);
        throw TapServerError.internalError();
      }
      return item;
    }
    if (options.get) {
      this.endpoints.push({
        call: (context) => {
          const value = getItem(context)[options.key];
          return value === undefined ? options.default : value;
        },
        meta: {
          path: options.path,
          method: TapRequestFrame.MethodType.GET,
          responseType: options.converter,
        },
      });
    }
    if (options.put) {
      this.endpoints.push({
        call: (context: CallContext<T[Key]>) => {
          getItem(context)[options.key] = context.body;
        },
        meta: {
          path: options.path,
          method: TapRequestFrame.MethodType.PUT,
          payloadType: options.converter,
        },
      });
    }
    if (options.post) {
      this.endpoints.push({
        call: (context: CallContext<T[Key]>) => {
          getItem(context)[options.key] = context.body;
        },
        meta: {
          path: options.path,
          method: TapRequestFrame.MethodType.POST,
          payloadType: options.converter,
        },
      });
    }
  }

  private async _startProtocols() {
    const errorProtocols: ServerComProtocol[] = [];
    const startedProtocls: ServerComProtocol[] = [];
    for (const p of this.protocols) {
      debug(`Start protocol ${p.debugString()}`);
      try {
        await p.start();
        console.log(`\t- ${p.debugString()} started!`);
        startedProtocls.push(p);
      } catch (err) {
        console.warn('\t- Cannot start protocol: ', p.debugString(), err);
        errorProtocols.push(p);
      }
    }
    return {
      startedProtocls,
      errorProtocols,
    };
  }
}

export namespace VirtualTap {
  export class Info {}
}
